import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import {
  getDbUserId,
  getExternalUserId,
  stripPlayCountFromUserId,
} from "../../functions/multiplayUserId";
import { setBackButtonUrl } from "../uiSlice";
import "./leaderboard.css";

function readStoredUser() {
  try {
    const raw =
      localStorage.getItem("userData") || sessionStorage.getItem("userData") || "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function rowTone(rank) {
  const r = Number(rank);
  if (r === 1) return "tone-1";
  if (r === 2) return "tone-2";
  if (r === 3) return "tone-3";
  return "tone-x";
}

function Leaderboard() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const userData = useMemo(() => readStoredUser(), []);
  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  const leaderboardBackUrl = useMemo(() => {
    const fromUser = String(userData?.backButtonRedirect ?? "").trim();
    if (fromUser && !/\/leaderboard\/?$/i.test(fromUser)) return fromUser;
    return "/thankyou";
  }, [userData]);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentUserId = getDbUserId(userData);
  const currentBaseUserId = getExternalUserId(userData);

  const normalizeIdentityText = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/[^a-z0-9 ]/g, "");

  /** One row per player (highest score); API also dedupes, this guards the UI. */
  const dedupedList = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const row of list) {
      const baseKey =
        stripPlayCountFromUserId(row?.userid) ||
        normalizeIdentityText(row?.name);
      if (!baseKey || seen.has(baseKey)) continue;
      seen.add(baseKey);
      out.push(row);
    }
    return out.map((row, i) => ({ ...row, rank: i + 1 }));
  }, [list]);

  const userNameCandidates = useMemo(() => {
    const firstName = String(userData.firstName ?? userData.first_name ?? "").trim();
    const lastName = String(userData.lastName ?? userData.last_name ?? "").trim();
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    const userId = String(
      userData.userId ??
        userData.userid ??
        userData.id ??
        userData.user_id ??
        ""
    ).trim();
    const email = String(userData.email ?? "").trim();
    const emailPrefix = email.includes("@") ? email.split("@")[0].trim() : "";
    const directName = String(userData.name ?? "").trim();

    const raw = [directName, fullName, firstName, lastName, userId, emailPrefix];
    return raw
      .map(normalizeIdentityText)
      .filter(Boolean)
      .filter((value, idx, arr) => arr.indexOf(value) === idx);
  }, [userData]);

  const isCurrentUserRow = (row) => {
    const rowUserId = String(row?.userid ?? "").trim();
    if (currentUserId && rowUserId === currentUserId) return true;
    if (
      currentBaseUserId &&
      stripPlayCountFromUserId(rowUserId) === currentBaseUserId
    ) {
      return true;
    }

    const rowName = normalizeIdentityText(row?.name ?? "");
    if (!rowName || userNameCandidates.length === 0) return false;

    return userNameCandidates.some(
      (candidate) =>
        candidate === rowName ||
        candidate.includes(rowName) ||
        rowName.includes(candidate)
    );
  };

  const visibleRows = useMemo(() => {
    const topFive = dedupedList.slice(0, 5).map((row) => ({
      ...row,
      displayRank: row.rank,
    }));
    if (!dedupedList.length) return [];

    const matchingByUserId = currentBaseUserId
      ? dedupedList.find(
          (row) =>
            stripPlayCountFromUserId(row?.userid) === currentBaseUserId ||
            String(row?.userid ?? "").trim() === currentUserId
        )
      : null;
    const matchingByName = dedupedList.find((row) => isCurrentUserRow(row));
    const matchingRow = matchingByUserId || matchingByName;

    if (!matchingRow) return topFive;

    const alreadyInTopFive = topFive.some((row) => row.rank === matchingRow.rank);
    if (alreadyInTopFive) return topFive;

    return [
      ...topFive,
      {
        ...matchingRow,
        displayRank: matchingRow.rank,
      },
    ];
  }, [dedupedList, currentUserId, currentBaseUserId, userNameCandidates]);

  useEffect(() => {
    dispatch(setBackButtonUrl(leaderboardBackUrl));
  }, [dispatch, leaderboardBackUrl]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const hadCommon = body.classList.contains("common-bg");
    html.classList.add("leaderboard-static-bg");
    body.classList.add("leaderboard-static-bg");
    body.classList.remove("common-bg");
    return () => {
      html.classList.remove("leaderboard-static-bg");
      body.classList.remove("leaderboard-static-bg");
      if (hadCommon) body.classList.add("common-bg");
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.history?.pushState) return;
    const url = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.history.pushState({ __leaderboardStay: 1 }, "", url);
  }, []);

  useEffect(() => {
    const sessionId = userData.sessionId;
    const organizationId = userData.organizationId;
    if (!adminToken || !backendBase || !sessionId || !organizationId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${backendBase}/getLeaderboard`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            sessionId,
            organizationId,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success && Array.isArray(data.data)) {
          setList(data.data);
          setError(null);
        } else {
          setError(data.message || "Failed to load leaderboard");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase, userData.organizationId, userData.sessionId]);

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-container">
        <div className="lb-card">
          <div className="lb-panel">
            <h1 className="lb-title">Leaderboard</h1>
            {loading ? (
              <div className="leaderboard-loading">Loading...</div>
            ) : error ? (
              <div className="leaderboard-error">{error}</div>
            ) : visibleRows.length === 0 ? (
              <div className="lb-empty">No players yet.</div>
            ) : (
              <div className="lb-list" role="list" aria-label="Leaderboard">
                <div className="lb-list-header">
                  <div className="lb-row-track">
                    <div className="lb-rank lb-rank--header">
                      <span className="lb-rank-header-text">Rank</span>
                    </div>
                    <div className="lb-bar lb-bar--header">
                      <span className="lb-header-label">Name</span>
                      <span className="lb-header-label">Points</span>
                      <span className="lb-header-label">Time</span>
                    </div>
                  </div>
                </div>
                {visibleRows.map((row) => {
                  const tone = rowTone(row.displayRank ?? row.rank);
                  const isMe = isCurrentUserRow(row);
                  const rankLabel = String(row.displayRank ?? row.rank ?? "");
                  const timeLabel = String(row.time ?? "").trim() || "—";
                  return (
                    <div
                      key={`${row.rank}-${row.name}-${row.points}-${timeLabel}`}
                      className={`lb-row ${tone} ${isMe ? "is-me" : ""}`}
                      role="listitem"
                      aria-label={`Rank ${rankLabel}, ${String(row.name || "").trim() || "Player"}, ${row.points} points, time ${timeLabel}`}
                    >
                      <div className="lb-row-track">
                        <div className={`lb-rank ${tone}`}>{rankLabel}</div>
                        <div className={`lb-bar ${tone}`}>
                          <span className="lb-name">
                            {String(row.name || "").toUpperCase()}
                          </span>
                          <span className="lb-points">{row.points}</span>
                          <span className="lb-time">{timeLabel}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
